/**
 * <TutorTrainingPage> — « Ta formation » du nouveau masteur : la vidéo, le quiz, le résultat (page).
 *
 * L'ancien `TutorTrainingScreen` : un écran, trois étapes (`phase`, contrôlée).
 *  - `video` : le titre et la phrase de la vidéo, la vidéo (`<TutorTrainingVideo>`), puis l'encart
 *    « Ensuite, un petit quiz » — ou, la formation validée, « Tu as validé ta formation » sur la
 *    tinte de progression — et « Passer le quiz » / « Repasser le quiz ».
 *  - `quiz` : la consigne (« Il en faut 16 sur 20 pour valider »), les vingt questions d'un coup,
 *    chacune dans sa carte (`<TutorTrainingQuestion>`), puis le compte des réponses, « Valider mes
 *    réponses » (actif quand tout est répondu) et « Revoir la vidéo ».
 *  - `result` : réussi, l'anneau du score (`<QuizScore>`, celui du quiz de l'élève), « Tu es
 *    prêt·e ! », la suite (la session avec l'équipe Tutor Excellence) et « Retour à l'accueil » ;
 *    raté, l'anneau, « Presque ! », « Repasser le quiz » et « Revoir la vidéo », puis la correction :
 *    les questions manquées d'abord, chacune gardant son numéro dans le quiz, dépliées sur leur
 *    bonne réponse (`<QuizReviewQuestion>`, la relecture de l'élève).
 * Le bouton de l'étape est en pied fixe ; deux actions au plus.
 *
 * Un tuteur actif n'a pas la carte de l'accueil, mais garde l'accès à cette page par « Vidéos
 * coaching » (`<TutorCoachingVideosPage>`) : la vidéo, et le quiz pour se tester.
 */
import React from "react"

import { Banner } from "../../atoms/Banner"
import { Button } from "../../atoms/Buttons/Button"
import { IconButton } from "../../atoms/Buttons/IconButton"
import { TextButton } from "../../atoms/Buttons/TextButton"
import { Stack } from "../../atoms/containers/Stack"
import { SectionLabel } from "../../atoms/typography/SectionLabel"
import { Text } from "../../atoms/typography/Text"
import { Title } from "../../atoms/typography/Title"
import { Card } from "../../molecules/Card"
import { CardList, CardListRow } from "../../molecules/CardList"
import { Header } from "../../molecules/Header"
import { QuizReviewQuestion } from "../../molecules/QuizReviewQuestion"
import { QuizScore } from "../../molecules/QuizScore"
import { TutorTrainingQuestion } from "../../molecules/TutorTrainingQuestion"
import { TutorTrainingVideo } from "../../molecules/TutorTrainingVideo"
import { PageTemplate } from "../../templates/PageTemplate"
import { type UIQuizReviewQuestion } from "../../../ports/primary/ui/types/studentQuizReview"
import { type UITutorTrainingQuestion, type UITutorTrainingVideo } from "../../../ports/primary/ui/types/tutorCompte"
import { TutorTrainingSkeleton } from "./TutorTrainingSkeleton"

export type TutorTrainingPhase =
    | {
          kind: "video"
          /** La formation est déjà validée. */
          passed: boolean
      }
    | {
          kind: "quiz"
          /** Les réponses choisies, par question. */
          answers: Record<number, string>
          /** L'envoi est parti : le bouton tourne. */
          submitting: boolean
          /** L'envoi n'est pas passé : sa phrase, au-dessus du bouton. */
          submitError?: string | null
      }
    | {
          kind: "result"
          passed: boolean
          correctCount: number
          /** Arrondi. */
          percentage: number
          /** La correction, question par question, dans l'ordre du quiz. */
          review: UIQuizReviewQuestion[]
      }

export type TutorTrainingPageProps = {
    onBack: () => void
    /** `null` tant que la formation n'est pas là. */
    video: UITutorTrainingVideo | null
    questions: UITutorTrainingQuestion[]
    /** Les bonnes réponses qu'il faut pour valider (16 sur 20). */
    neededCount: number
    phase: TutorTrainingPhase
    loading?: boolean
    error?: unknown
    onRetry?: () => void
    /** Le lecteur de la vidéo, posé par l'écran. Absent : l'affiche. */
    player?: React.ReactNode
    onPlayVideo: () => void
    /** « Passer le quiz », « Repasser le quiz » (depuis la vidéo ou après un échec). */
    onStartQuiz: () => void
    onSelectAnswer: (questionId: number, answer: string) => void
    onSubmit: () => void
    onReviewVideo: () => void
    /** « Retour à l'accueil », après la réussite. */
    onDone: () => void
    testID?: string
}

/** La colonne d'une lecture : la vidéo et les questions ne s'étalent pas sur 1 100 px. */
const TRAINING_WIDTH = 720

const TUTOR_TRAINING_TEXTS = {
    title: "Ta formation",
    nextQuiz: "Ensuite, un petit quiz",
    nextQuizBody: (count: number) =>
        `${count} questions pour vérifier que tout est clair avant ton premier cours. Prends le temps de regarder la vidéo en entier, les réponses y sont.`,
    passedTitle: "Tu as validé ta formation",
    passedBody: "Tu peux revoir la vidéo quand tu veux, et repasser le quiz pour te tester.",
    startQuiz: "Passer le quiz",
    retakeQuiz: "Repasser le quiz",
    rules: (needed: number, total: number) =>
        `Une seule bonne réponse par question. Il en faut ${needed} sur ${total} pour valider, et tu peux repasser le quiz autant de fois que tu veux.`,
    answered: (count: number, total: number) => `${count} / ${total} réponses`,
    submit: "Valider mes réponses",
    reviewVideo: "Revoir la vidéo",
    ready: "Tu es prêt·e !",
    readyBody:
        "Bravo, ta formation est validée. Dernière étape avant ton premier cours : une courte session avec l'équipe Tutor Excellence, pour vérifier ensemble que tout est en place. Elle te contacte pour la caler.",
    done: "Retour à l'accueil",
    almost: (needed: number) =>
        `Presque ! Il faut ${needed} bonnes réponses pour valider. Voici les bonnes réponses, relis-les puis retente ta chance.`,
    correction: "Ta correction",
}

const T = TUTOR_TRAINING_TEXTS

export function TutorTrainingPage(props: TutorTrainingPageProps) {
    const {
        onBack,
        video,
        questions,
        neededCount,
        phase,
        loading = false,
        error,
        onRetry,
        player,
        onPlayVideo,
        onStartQuiz,
        onSelectAnswer,
        onSubmit,
        onReviewVideo,
        onDone,
        testID,
    } = props

    const header = (
        <Header
            title={T.title}
            left={
                <IconButton
                    iconName="chevron-left"
                    appearance="transparent"
                    iconColor="primary"
                    onPress={onBack}
                    accessibilityLabel="Retour"
                />
            }
        />
    )

    const total = questions.length
    let body: React.ReactNode = null
    let footer: React.ReactNode

    if (video != null && phase.kind === "video") {
        body = (
            <Stack gap="4">
                <Stack gap="2">
                    <Title size="text-xl">{video.title}</Title>
                    <Text>{video.description}</Text>
                </Stack>
                <TutorTrainingVideo embedded={video.embedUrl != null} player={player} onPlay={onPlayVideo} />
                <Card p="4" elevation="none" background={phase.passed ? "successLight" : "primary-soft"}>
                    <Stack gap="1">
                        <Text weight="bold" color={phase.passed ? "success-strong" : "primary"}>
                            {phase.passed ? T.passedTitle : T.nextQuiz}
                        </Text>
                        <Text>{phase.passed ? T.passedBody : T.nextQuizBody(total)}</Text>
                    </Stack>
                </Card>
            </Stack>
        )
        footer = <Button label={phase.passed ? T.retakeQuiz : T.startQuiz} fullWidth onPress={onStartQuiz} />
    } else if (phase.kind === "quiz") {
        const answeredCount = questions.filter((q) => phase.answers[q.id] != null).length
        body = (
            <Stack gap="3">
                <Text>{T.rules(neededCount, total)}</Text>
                {questions.map((question, index) => (
                    <TutorTrainingQuestion
                        key={question.id}
                        index={index}
                        question={question}
                        selected={phase.answers[question.id]}
                        onSelect={(answer) => onSelectAnswer(question.id, answer)}
                        disabled={phase.submitting}
                    />
                ))}
            </Stack>
        )
        footer = (
            <Stack gap="2" align="center">
                {phase.submitError != null && phase.submitError !== "" && (
                    <Banner message={phase.submitError} color="danger" icon="exclamation-circle" tinted />
                )}
                <Text size="text-md" color="text-muted" center>
                    {T.answered(answeredCount, total)}
                </Text>
                <Button
                    label={T.submit}
                    fullWidth
                    disabled={answeredCount < total}
                    loading={phase.submitting}
                    onPress={onSubmit}
                />
                <TextButton label={T.reviewVideo} onPress={onReviewVideo} disabled={phase.submitting} />
            </Stack>
        )
    } else if (phase.kind === "result") {
        const score = (
            <QuizScore
                score={phase.correctCount}
                total={phase.review.length}
                percentage={phase.percentage}
                passed={phase.passed}
            />
        )
        if (phase.passed) {
            body = (
                <Stack gap="4" align="center">
                    {score}
                    <Title center>{T.ready}</Title>
                    <Text center>{T.readyBody}</Text>
                </Stack>
            )
            footer = <Button label={T.done} fullWidth onPress={onDone} />
        } else {
            // Les manquées d'abord ; chacune garde son numéro dans le quiz.
            const ordered = phase.review
                .map((question, index) => ({ question, index }))
                .sort((a, b) => Number(a.question.result === "right") - Number(b.question.result === "right"))
            const missedIds = phase.review.filter((q) => q.result !== "right").map((q) => q.id)
            body = (
                <Stack gap="4">
                    <Stack gap="3" align="center">
                        {score}
                        <Text center>{T.almost(neededCount)}</Text>
                    </Stack>
                    <Stack gap="2">
                        <SectionLabel>{T.correction}</SectionLabel>
                        <CardList>
                            {ordered.map(({ question, index }) => (
                                <CardListRow key={question.id} py="0">
                                    <QuizReviewQuestion
                                        index={index}
                                        question={question}
                                        defaultExpanded={missedIds.includes(question.id)}
                                    />
                                </CardListRow>
                            ))}
                        </CardList>
                    </Stack>
                </Stack>
            )
            footer = (
                <Stack gap="2" align="center">
                    <Button label={T.retakeQuiz} fullWidth onPress={onStartQuiz} />
                    <TextButton label={T.reviewVideo} onPress={onReviewVideo} />
                </Stack>
            )
        }
    }

    return (
        <PageTemplate
            testID={testID}
            header={header}
            loading={loading}
            skeleton={<TutorTrainingSkeleton />}
            error={error}
            onRetry={onRetry}
            padding="4"
            maxContentWidth={TRAINING_WIDTH}
            footer={loading || error != null ? undefined : footer}
        >
            {body}
        </PageTemplate>
    )
}
